import React, { useState } from 'react'
import { SERIES, CHROME } from '../../lib/viz'

/**
 * Horizontal bars for "compare magnitude across named things" — top products,
 * revenue by category.
 *
 * Horizontal, not vertical: the labels are garment names like "Mens Stretch
 * Pique Short Sleeve Golfer". As column labels those either rotate 45° or get
 * truncated to uselessness; as row labels they simply read.
 *
 * One colour for every bar, on purpose. The bars already encode magnitude by
 * length, so colouring each one differently would add a second, redundant
 * encoding that implies the colours mean something. Categorical colour is for
 * when the series ARE the subject; here the quantity is.
 *
 * Built from divs rather than SVG — a bar chart is a list with one length per
 * row, and HTML gives text wrapping, ellipsis and hit targets for free.
 */
const BarList = ({
  data,
  labelKey = 'name',
  valueKey = 'revenue',
  formatValue = (v) => v,
  secondary = null, // (row) => string, shown under the label
  limit = 8,
  emptyMessage = 'Nothing to show yet.',
  onSelect = null,
}) => {
  const [hovered, setHovered] = useState(null)

  const rows = (data ?? [])
    .filter((r) => (Number(r[valueKey]) || 0) > 0)
    .sort((a, b) => (Number(b[valueKey]) || 0) - (Number(a[valueKey]) || 0))
    .slice(0, limit)

  if (rows.length === 0) {
    return <div className="py-8 text-center text-sm text-ink-muted">{emptyMessage}</div>
  }

  // Scale to the largest bar rather than to a round number: the comparison here
  // is between rows, and a value axis would be chrome nobody reads.
  const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0))

  return (
    <ul className="space-y-2.5">
      {rows.map((row, index) => {
        const value = Number(row[valueKey]) || 0
        const widthPct = max > 0 ? Math.max((value / max) * 100, 1.5) : 0
        const isHovered = hovered === index
        const interactive = Boolean(onSelect)

        return (
          <li
            key={row.id ?? row[labelKey] ?? index}
            onMouseEnter={() => setHovered(index)}
            onMouseLeave={() => setHovered(null)}
            onClick={interactive ? () => onSelect(row) : undefined}
            className={`group relative ${interactive ? 'cursor-pointer' : ''}`}
          >
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <span className="truncate text-[13px] font-medium text-ink" title={row[labelKey]}>
                {row[labelKey]}
              </span>
              {/* Direct label on every row. The bars are the same colour, so the
                  number is what distinguishes them — and it means the chart is
                  still readable with no colour vision at all. */}
              <span className="shrink-0 text-[13px] font-semibold tabular-nums text-ink">
                {formatValue(value)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <div
                className="relative h-2 flex-1 overflow-hidden rounded-full"
                style={{ background: CHROME.grid }}
              >
                <div
                  className="h-full rounded-full transition-[width,opacity] duration-500 ease-out"
                  style={{
                    width: `${widthPct}%`,
                    background: SERIES,
                    opacity: hovered === null || isHovered ? 1 : 0.55,
                  }}
                />
              </div>
            </div>

            {secondary && (
              <div className="mt-0.5 text-2xs text-ink-muted">{secondary(row)}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export default BarList
